import AppNavigator from './AppNavigator';
import { ThemeProvider } from 'styled-components';
import { Provider } from 'react-redux';
import store from '../src/state/store';
import { AlertsProvider, useAlert } from './state/Alerts/AlertsContext';
import AlertCustom from './components/AlertCustom';
import { useEffect } from 'react';
import { ALERT_RESPONSES } from './utils/constants';
import { useDispatch } from 'react-redux';
import actions from './state/actions';

const theme = {
  screenSizes: {
    mobileS: '20rem',
    mobileM: '23.4375rem',
    mobileL: '26.5625rem',
    tablet: '48rem',
    laptop: '64rem',
    laptopL: '90rem',
    desktop: '160rem'
  }
}

const GlobalAlert = () => {
  const { state, hideAlert } = useAlert();
  if (!state.visible) return null;
  const alertType = state.type === ALERT_RESPONSES.SUCCESS ? 'success' : 'error';
  return <AlertCustom message={state.message} type={alertType} onClose={hideAlert} />;
};

const AppWrapper = () => {

  return (
    <Provider store={store}>
      <AlertsProvider>
        <GlobalAlert />
        <App />
      </AlertsProvider>
    </Provider>
  );
};

const App = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    // Rehydrate auth state from storage on app startup
    // This runs in background without blocking UI for better anonymous user experience
    dispatch(actions.auth.rehydrateAuth());
  }, [dispatch]);

  useEffect(() => {
    console.log('🔍 [APP] Checking for MercadoPago return URL');
    
    const urlParams = new URLSearchParams(window.location.search);
    
    // MercadoPago returns these parameters:
    // - status: 'approved' | 'rejected' | 'pending' (primary status)
    // - collection_status: same as status (legacy)
    // - external_reference: our booking ID
    // - payment_id / collection_id: MercadoPago payment ID
    // - preference_id: MercadoPago preference ID
    // - payment_type: 'credit_card' | 'account_money' | etc
    
    const status = urlParams.get('status');  // PRIMARY - MercadoPago sends 'status', not 'payment_status'
    const collectionStatus = urlParams.get('collection_status');  // Legacy fallback
    const bookingId = urlParams.get('external_reference');
    const collectionId = urlParams.get('collection_id');
    const preferenceId = urlParams.get('preference_id');
    const paymentId = urlParams.get('payment_id');
    const paymentType = urlParams.get('payment_type');
    const merchantOrderId = urlParams.get('merchant_order_id');

    // Use status or fall back to collection_status
    const paymentStatus = status || collectionStatus;

    console.log('🔍 [APP] URL Parameters:', {
      status,
      collectionStatus,
      paymentStatus,
      bookingId,
      collectionId,
      preferenceId,
      paymentId,
      paymentType,
      merchantOrderId,
      fullURL: window.location.href
    });

    // Check if this is a MercadoPago return (need both status and booking reference)
    if (paymentStatus && bookingId) {
      console.log('✅ [APP] Payment return detected!');
      console.log('💳 [APP] Payment Status:', paymentStatus);
      console.log('📋 [APP] Booking ID:', bookingId);

      // Store for UI to display
      const paymentReturnData = {
        status: paymentStatus,  // 'approved' | 'rejected' | 'pending'
        bookingId,
        collectionId,
        collectionStatus,
        preferenceId,
        paymentId,
        paymentType,
        merchantOrderId,
        timestamp: new Date().toISOString()
      };

      sessionStorage.setItem('paymentReturn', JSON.stringify(paymentReturnData));
      console.log('💾 [APP] Stored payment return data in sessionStorage:', paymentReturnData);

      // Clean URL (remove MercadoPago params)
      const cleanURL = window.location.pathname;
      console.log('🧹 [APP] Cleaning URL from:', window.location.href);
      console.log('🧹 [APP] Cleaning URL to:', cleanURL);
      window.history.replaceState({}, '', cleanURL);

      // Display feedback to user based on status
      if (paymentStatus === 'approved') {
        console.log('🎉 [APP] Payment approved! Booking confirmed.');
        // TODO: Show success toast/modal
        // TODO: Navigate to booking confirmation page
      } else if (paymentStatus === 'pending') {
        console.log('⏳ [APP] Payment pending. Waiting for confirmation.');
        // TODO: Show pending message
      } else if (paymentStatus === 'rejected') {
        console.log('❌ [APP] Payment rejected.');
        // TODO: Show error message with option to retry
      }
    } else {
      console.log('ℹ️ [APP] No MercadoPago return parameters found');
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <div style={{ fontFamily: "'Inter', sans-serif" }}>
        <AppNavigator />
      </div>
    </ThemeProvider>
  );
};

export default AppWrapper;
